import Long from 'long';
import { PPCPointCloudGeometry } from '.';
import { EventDispatcher } from '../../EventDispatcher';
import { ChunkData } from '../../ppcreader';
import { EnumFileType, EnumHierarchyFileType, PPCFile } from './PPCFile';

export class PPCChunk extends EventDispatcher {
  info: ChunkData;
  ppcGeometry: PPCPointCloudGeometry;
  id: Long;
  pointsAmount: number;
  nodes: unknown[];
  loads: { [key: string]: boolean } = {};
  fileCache: Map<string, PPCFile> = new Map();
  folder: string;
  identifier: Long;
  level: number;
  loaded?: boolean;

  constructor({ info, ppcGeometry }: { info: ChunkData; ppcGeometry: PPCPointCloudGeometry }) {
    super();
    this.ppcGeometry = ppcGeometry;
    this.folder = this.ppcGeometry.ppchReader.mDataFolder.replace(/\/+$/, '').replace(/\\/g, '');
    this.identifier = info.mFileNameId;
    this.level = info.mLevel;
    this.id = info.mChunkId;
    this.info = info;
    this.pointsAmount = this.info.mNrVoxels;
    this.fileCache = new Map();
    this.nodes = [];
  }

  protected getFile(args: { fileType: EnumFileType }) {
    const { fileType } = args;
    const key = `${this.identifier}_${fileType}`;
    let file = this.fileCache.get(key);
    if (!file) {
      file = new PPCFile({ fileIdentifier: this.identifier, fileType, folder: this.folder, level: this.level });
      this.fileCache.set(key, file);
    }
    return file;
  }

  async loadData({ type }: { type: EnumFileType }) {
    const file = this.getFile({ fileType: type });
    const result = await file.load({ pointsAmount: this.info.mNrVoxels });
    return result;
  }

  /**
   * Clear the file cache to free memory
   */
  clearFileCache() {
    this.fileCache.clear();
  }

  /**
   * Dispose of this chunk and free memory
   */
  dispose() {
    this.fileCache.clear();
    this.nodes = [];
    this.loads = {};
  }
}

export class PPCDataChunk extends PPCChunk {
  private _isDisposed = false;

  /**
   * Check if this chunk has been disposed
   */
  isDisposed(): boolean {
    return this._isDisposed;
  }

  dispose() {
    this._isDisposed = true;
    super.dispose();
  }
}

export class PPCHierarchyChunk extends PPCChunk {
  private loadPromise?: Promise<{ childInfo: number[] | undefined; tile3DOffsets: number[] | undefined }>;
  private loadFailed = false;
  private loadedWithEmptyData = false;
  private _isDisposed = false;

  async load() {
    // Don't load if disposed
    if (this._isDisposed) {
      return { childInfo: undefined, tile3DOffsets: undefined };
    }

    // If previous load failed or returned empty data, clear the promise to allow retry
    if (this.loadFailed || this.loadedWithEmptyData) {
      this.loadPromise = undefined;
      this.loadFailed = false;
      this.loadedWithEmptyData = false;
    }

    // Return cached promise if already loading/loaded to prevent duplicate work
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this._doLoad()
      .then((result) => {
        // Check if disposed during load
        if (this._isDisposed) {
          return { childInfo: undefined, tile3DOffsets: undefined };
        }
        // Check if we got empty data (fetch failed silently)
        // Both being undefined means the fetch probably failed
        if (result.childInfo === undefined && result.tile3DOffsets === undefined) {
          this.loadedWithEmptyData = true;
        }
        return result;
      })
      .catch((error) => {
        // Check if disposed during load - don't mark as failed if disposed
        if (this._isDisposed) {
          return { childInfo: undefined, tile3DOffsets: undefined };
        }
        // On error, mark as failed so next call can retry
        this.loadFailed = true;
        throw error;
      });

    return this.loadPromise;
  }

  private async _doLoad(): Promise<{ childInfo: number[] | undefined; tile3DOffsets: number[] | undefined }> {
    const promises = [];
    let childInfo: number[] | undefined = undefined;
    let tile3DOffsets: number[] | undefined = undefined;

    // Early exit if disposed
    if (this._isDisposed) {
      return { childInfo, tile3DOffsets };
    }

    if (this.level < this.ppcGeometry.depth)
      promises.push(
        (async () => {
          if (this._isDisposed) return;
          const result = await this.loadData({ type: EnumHierarchyFileType.CHILD_INFO });
          if (!this._isDisposed) {
            childInfo = result;
          }
        })(),
      );
    if (this.level <= this.ppcGeometry.depth)
      promises.push(
        (async () => {
          if (this._isDisposed) return;
          const result = await this.loadData({ type: EnumHierarchyFileType.TILE_3D_OFFSET });
          if (!this._isDisposed) {
            tile3DOffsets = result;
          }
        })(),
      );
    await Promise.all(promises);
    return { childInfo, tile3DOffsets };
  }

  /**
   * Check if this chunk has been disposed
   */
  isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Clear the cached load promise to allow retry
   */
  clearLoadPromise() {
    this.loadPromise = undefined;
    this.loadFailed = false;
    this.loadedWithEmptyData = false;
  }

  dispose() {
    this._isDisposed = true;
    super.dispose();
    this.loadPromise = undefined;
    this.loadFailed = false;
    this.loadedWithEmptyData = false;
  }
}
